import { BaseWebComponentView } from '../../framework/BaseWebComponentView';
import {
  CardMessage,
  featureToId,
  GzLandingPageCompanyType,
  IntegrationFeature,
  IntegrationFeatures,
  LandingPageData,
  provisionedStatusMap,
  TEMPLATE_AREAS,
} from '../types';
import {
  COMMON_TEMPLATE_AREAS,
  getCommonWidgets,
  getSpecificWidgets,
  getSsoSingleIntegrationProps,
  getSsoWidgetsProps,
  repeatArea,
} from '../utils';
import { LandingPageTemplateWebComponent } from '../../landingPageTemplate';
import { LPWidgetConfig } from '../../landingPageTemplate/types';
import { FontSizes, TemplateState } from '../../types/common/types';
import { translateFn } from '../../utils/translator.helper';

export class GzLandingPageAppView extends BaseWebComponentView {
  dom: {
    $wrapper: HTMLElement;
    $landingPageTemplate: LandingPageTemplateWebComponent;
    $skeleton: HTMLElement;
  };

  public getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $landingPageTemplate: this.shadow.querySelector('gz-landing-page-template'),
      $skeleton: this.shadow.querySelector('.skeleton'),
    };
  }

  private getIntegrationWidgetConfig(integrationFeatures: IntegrationFeatures): LPWidgetConfig {
    const availableFeatures: IntegrationFeature[] = (Object.keys(integrationFeatures) as IntegrationFeature[]).filter(
      key => integrationFeatures[key].hasFeature
    );

    if (availableFeatures.length < 1) {
      return {
        id: 'integration',
        content: 'gz-text',
        gridArea: TEMPLATE_AREAS.INTEGRATIONS,
        props: {},
      };
    }

    if (availableFeatures.length === 1) {
      const feature = availableFeatures[0];
      const widgetProps = getSsoSingleIntegrationProps()[feature];
      const id = featureToId[feature];

      const isProvisioned = integrationFeatures[feature].settings.provisionStatus === provisionedStatusMap[id];

      return {
        id: 'integrations',
        content: 'gz-sso-single-integration-widget',
        gridArea: TEMPLATE_AREAS.INTEGRATIONS,
        props: {
          isProvisioned,
          ...widgetProps,
        },
      };
    }

    const ssoWidgetProps = getSsoWidgetsProps();
    const ssoWidgets = availableFeatures.map(feature => ({
      ...ssoWidgetProps[feature],
      provisionStatus: integrationFeatures[feature]?.settings?.provisionStatus || 0,
    }));

    return {
      id: 'integrations',
      content: 'gz-sso-integration-widget',
      gridArea: TEMPLATE_AREAS.INTEGRATIONS,
      props: { options: ssoWidgets },
    };
  }

  private configWidgets(
    landingPageDataset: LandingPageData,
    gzVersion: string,
    messages: CardMessage[] | undefined,
    rssFeedUrls: string[] | undefined,
    dynamicEventWidgetInfo: WidgetInfo | undefined,
    dynamicPromoWidgetInfo: WidgetInfo | undefined
  ) {
    const integrationFeatures = landingPageDataset.integrationFeatures;

    const widgets = [
      this.getIntegrationWidgetConfig(integrationFeatures),
      ...getCommonWidgets(landingPageDataset),
      ...getSpecificWidgets(landingPageDataset, messages, dynamicEventWidgetInfo, dynamicPromoWidgetInfo),
    ];

    // add gz version to welcome widget
    const welcomeWidgetIndex = widgets.findIndex(widget => widget.id === 'welcome');
    widgets[welcomeWidgetIndex].props = {
      productVersion: gzVersion.split('.').slice(0, 2).join('.'),
      companyType: landingPageDataset.companyType,
    };

    const newsFeedWidgetIndex = widgets.findIndex(widget => widget.id === 'news');
    widgets[newsFeedWidgetIndex].props = {
      rssFeedUrls: rssFeedUrls,
    };

    this.dom.$landingPageTemplate.widgets = widgets;
  }

  private configTemplateAreas(landingPageDataset: LandingPageData) {
    // override template areas based on company type by using splice
    // each entry in the array is a row in the grid
    const templateAreas = [...COMMON_TEMPLATE_AREAS];

    switch (landingPageDataset.companyType) {
      case GzLandingPageCompanyType.CSPM_STANDALONE:
        templateAreas.push([...repeatArea(TEMPLATE_AREAS.MASTERCLASS, 6)]);
        templateAreas.splice(1, 0, [
          ...repeatArea(TEMPLATE_AREAS.HEADER, 4),
          ...repeatArea(TEMPLATE_AREAS.HEADER_TOP, 2),
        ]);
        templateAreas.splice(3, 0, [...repeatArea(TEMPLATE_AREAS.INTEGRATIONS, 6)]);
        break;
      case GzLandingPageCompanyType.CUSTOMER:
        templateAreas.push([...repeatArea(TEMPLATE_AREAS.MASTERCLASS, 6)]);
        templateAreas.splice(1, 0, [
          ...repeatArea(TEMPLATE_AREAS.HEADER, 4),
          ...repeatArea(TEMPLATE_AREAS.HEADER_MIDDLE, 2),
        ]);
        templateAreas.splice(3, 0, [...repeatArea(TEMPLATE_AREAS.PRODUCT_TRIALS, 6)]);
        templateAreas.push([...repeatArea(TEMPLATE_AREAS.INTEGRATIONS, 6)]);
        break;
      case GzLandingPageCompanyType.MSP:
        templateAreas.push([...repeatArea(TEMPLATE_AREAS.INTEGRATIONS, 6)]);
        templateAreas.splice(2, 0, [
          ...repeatArea(TEMPLATE_AREAS.BODY_LEFT_TOP, 2),
          ...repeatArea(TEMPLATE_AREAS.BODY_RIGHT_TOP, 4),
        ]);
        templateAreas.splice(3, 0, [
          ...repeatArea(TEMPLATE_AREAS.BODY_LEFT_BOTTOM, 2),
          ...repeatArea(TEMPLATE_AREAS.BODY_RIGHT_BOTTOM, 4),
        ]);
        break;
      default:
        break;
    }

    this.dom.$landingPageTemplate.templateAreas = templateAreas;
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);

    this.dom.$skeleton.hidden = false;
    this.dom.$landingPageTemplate.hidden = true;
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.dom.$skeleton.remove();
    this.dom.$landingPageTemplate.hidden = false;
  }

  buildSuccessView(gzVersion: string) {
    const dataset = this.shadow.host.controller.component.dataset['landingPage'] || '';
    const landingPageData = JSON.parse(dataset);

    const messages = this.shadow.host.controller.messages;
    const rssFeedUrls = this.shadow.host.controller.rssFeedUrls;
    const dynamicEventWidgetInfo = this.shadow.host.controller.dynamicEventWidgetInfo;
    const dynamicPromoWidgetInfo = this.shadow.host.controller.dynamicPromoWidgetInfo;

    this.configTemplateAreas(landingPageData);
    this.configWidgets(
      landingPageData,
      gzVersion,
      messages,
      rssFeedUrls,
      dynamicEventWidgetInfo,
      dynamicPromoWidgetInfo
    );
  }

  buildErrorView(error: Error) {
    console.error(error);
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);

    this.dom.$wrapper.innerHTML = `
      <gz-image-repo state="error" class="error-state">
        <gz-title class="emptyViewTitle" fontSize='${FontSizes.HEADING_4}' fontWeight="500">${translateFn(
      'Something went wrong'
    )}</gz-title>
        <gz-text class="emptyViewText" fontSize="13">${translateFn(
          'There was an error. Please try again later.'
        )}</gz-text>
      </gz-image-repo>`;
  }
}
